import { Component, Injectable, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CommonService } from 'abs-ngx-library/src/lib/core';

@Component({
  selector: 'quantity-stepper-simple-al-v1',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './quantity-stepper-simple-al-v1.component.html',
  styleUrl: './quantity-stepper-simple-al-v1.component.scss'
})

@Injectable()
export class QuantityStepperSimpleAlV1Component {
  @Input() modifier: any;
  @Input() option: any;
  @Output() qtyStepperInfoEmitter = new EventEmitter<any>();
  
  // Live announcement message for screen readers
  public announcementMessage: string = '';
  constructor(public commonService: CommonService) {}
  
  get quantity(): number {
    // Prevent stepper for 'No' customization options
    if (this.option?.name?.startsWith('No')) return 0;
    // Get quantity from CommonService customizationData signal
    const customizationData = this.commonService.customizationData();
    if (!customizationData.selectedOptions) return 1;
    const found = customizationData.selectedOptions.find(sel => sel?.upcId === this.option?.upcId);
    return found?.qty || (this.option?.isSelected ? 1 : 0);
  }

  /**
   * Unified aria label getter for increment/decrement
   */
  public getAriaLabel(action: 'decrement' | 'increment'): string {
    const name = this.option?.name || 'item';
    if (action === 'decrement') {
      if (this.quantity === 1) {
        return `Remove quantity 1 unit of ${name}`;
      }
      return `Decrease quantity 1 unit of ${name}`;
    }
    // increment
    if (this.isMaxQuantityReached()) {
      return `Increase quantity 1 unit of ${name} button disabled`;
    }
    return `Increase quantity 1 unit of ${name}`;
  }

  /**
   * Getter for aria-label for quantity section
   */
  public get quantityAriaLabel(): string {
    return `${this.quantity} unit${this.quantity === 1 ? '' : 's'} of ${this.option?.name} selected`;
  }

  /**
   * Announces a message via the sr-only live region immediately
   */
  private announceMessage(msg: string) {
    this.announcementMessage = msg;
  }

  /**
   * Checks if the current quantity has reached the maximum allowed for this modifier
   */
  public isMaxQuantityReached(): boolean {
    if (!this.modifier?.maxSelect) {
      return false;
    }
    // For radio buttons or single-select, disable increment if one is selected
    if (this.modifier.controlType === 'radio' || this.modifier.maxSelect === 1) {
      return this.quantity === 1;
    }
    // Use the new commonService method with signal's selectedOptions
    const customizationData = this.commonService.customizationData();
    return this.commonService.isMaxQuantityReachedForModifier(this.modifier, customizationData.selectedOptions);
  }

  public onQuantityChange(newQuantity: number, event?: Event): void {
    // Prevent quantity change for 'No' customization options
    if (this.option?.name?.startsWith('No')) return;
    // Prevent the click from bubbling to the parent label which would toggle the checkbox
    try {
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
    } catch (e) {
      // ignore
    }
    const positionText = this.getPositionText();

    // Special case: if current quantity is 1 and user clicks decrement (trash icon)
    // emit 0 to signal parent to uncheck the option
    if (this.quantity === 1 && newQuantity < 1) {
      // Announce the removal action
      this.announceMessage(`${this.option?.name || 'Item'} checkbox unchecked ${positionText}`);
      // Prevent parent label from toggling the checkbox as a result of this click
      try {
        (this.option as any).deSelect = true;
      } catch (e) {
        // ignore
      }
      // Delay the emit slightly to allow the live region to update and be announced
      setTimeout(() => {
        this.qtyStepperInfoEmitter.emit(0);
        // After unchecking, set focus back to the checkbox input for this option
        const upcId = this.option?.upcId;
        const input = document.querySelector(`input[data-upc-id='${upcId}']`);
        if (input && typeof (input as HTMLElement).focus === 'function') {
          (input as HTMLElement).focus();
        }
      }, 100);
      return;
    }
    // Ensure quantity doesn't go below 1 for normal cases
    if (newQuantity < 1) {
      newQuantity = 1;
    }
    // Compute announcement message including additional charge if present
    const unitsText = `${newQuantity} ${newQuantity === 1 ? 'unit' : 'units'} of ${this.option?.name || 'item'} updated`;
    if (this.option?.price) {
      const additional = (this.option.price || 0) * newQuantity;
      const formatted = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(additional);
      this.announceMessage(`${unitsText} ${positionText} ${formatted} additional charge`);
    } else {
      this.announceMessage(`${unitsText} ${positionText}`);
    }

    this.qtyStepperInfoEmitter.emit(newQuantity);
    // Restore focus to the button after click
    if (event && event.target && (event.target as HTMLElement).focus) {
      setTimeout(() => {
        (event.target as HTMLElement).focus();
      }, 0);
    }
  }

  /**
   * Helper to return position text like "1 of 5" for the option within its modifier
   */
  private getPositionText(): string {
    try {
      const options = this.modifier?.options || [];
      const index = options.findIndex((o: any) => o.upcId === this.option?.upcId);
      if (index > -1) {
        return `${index + 1} of ${options.length}`;
      }
    } catch (e) {
      // ignore
    }
    return '';
  }
}
